import React, { useEffect, useRef, useState } from "react";
import { Send, MessageSquarePlus, Inbox, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState, Loader, StatusBadge } from "@/components/dashboard/ui";
import { MESSAGE_CATEGORIES } from "@/data/constants";
import { trashAction } from "@/lib/trash";

export default function Messages({ admin = false }) {
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [nc, setNc] = useState({ subject: "", category: "Question générale", message: "" });
  const endRef = useRef(null);

  const loadConvs = () => api.get("/conversations").then(({ data }) => setConvs(data || []));
  useEffect(() => { loadConvs(); }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/conversations/${active.conversation_id}/messages`).then(({ data }) => setMsgs(data || []));
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/conversations/${active.conversation_id}/messages`, { text });
      setMsgs((m) => [...m, data]); setText(""); loadConvs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createConv = async () => {
    if (!nc.subject || !nc.message) { toast.error("Sujet et message requis"); return; }
    try {
      const { data } = await api.post("/conversations", nc);
      setNewOpen(false); setNc({ subject: "", category: "Question générale", message: "" });
      await loadConvs(); setActive(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delConv = async (conv) => {
    if (!window.confirm("Déplacer cette conversation vers la corbeille ?")) return;
    try {
      await trashAction("conversations", [conv.conversation_id], "delete");
      toast.success("Conversation déplacée vers la corbeille");
      if (active?.conversation_id === conv.conversation_id) { setActive(null); setMsgs([]); }
      loadConvs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (convs === null) return <Loader />;

  return (
    <div>
      <PageHeader title="Messagerie" subtitle={admin ? "Boîte de réception partagée" : "Échangez avec notre équipe"}
        action={!admin && <Button className="bg-primary" onClick={() => setNewOpen(true)} data-testid="new-conversation-btn"><MessageSquarePlus className="h-4 w-4 mr-1.5" />Nouveau message</Button>} />
      <div className="grid lg:grid-cols-3 gap-4 h-[calc(100vh-14rem)]">
        <div className="bg-white rounded-xl border border-slate-200 overflow-y-auto custom-scroll">
          {convs.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">Aucune conversation</p>}
          {convs.map((c) => (
            <div key={c.conversation_id} onClick={() => setActive(c)} data-testid={`conv-${c.conversation_id}`}
              className={`group relative w-full text-left p-4 pr-9 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${active?.conversation_id === c.conversation_id ? "bg-primary/5" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm text-slate-900 truncate">{c.subject}</span>
                {c.unread > 0 && <span className="h-5 min-w-5 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>}
              </div>
              {admin && <p className="text-xs text-slate-500 mt-0.5">{c.client_name}</p>}
              <div className="mt-1.5 flex items-center gap-2"><span className="text-xs text-slate-400">{c.category}</span><StatusBadge status={c.status} /></div>
              {admin && <button onClick={(e) => { e.stopPropagation(); delConv(c); }} title="Déplacer vers la corbeille" data-testid={`conv-delete-${c.conversation_id}`}
                className="absolute right-2 top-3 p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center"><EmptyState icon={Inbox} title="Sélectionnez une conversation" /></div>
          ) : (<>
            <div className="p-4 border-b border-slate-100"><h3 className="font-semibold text-slate-900">{active.subject}</h3><p className="text-xs text-slate-500">{active.category}{admin && ` · ${active.client_name}`}</p></div>
            <div className="flex-1 overflow-y-auto custom-scroll p-4 space-y-3">
              {msgs.map((m) => {
                const mine = m.sender_id === user.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-primary text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                      {!mine && <p className="text-xs font-medium opacity-70 mb-0.5">{m.sender_name}</p>}
                      <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-slate-100 flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message..." data-testid="message-input" />
              <Button className="bg-primary" onClick={send} data-testid="message-send"><Send className="h-4 w-4" /></Button>
            </div>
          </>)}
        </div>
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau message</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Sujet</Label><Input value={nc.subject} onChange={(e) => setNc({ ...nc, subject: e.target.value })} className="mt-1" data-testid="nc-subject" /></div>
            <div><Label>Catégorie</Label><Select value={nc.category} onValueChange={(v) => setNc({ ...nc, category: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{MESSAGE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Message</Label><Textarea rows={4} value={nc.message} onChange={(e) => setNc({ ...nc, message: e.target.value })} className="mt-1" data-testid="nc-message" /></div>
          </div>
          <DialogFooter><Button className="bg-primary" onClick={createConv} data-testid="nc-submit">Envoyer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
